/**
 * Precificação dos exames.
 *
 * A conta é **duplicada** do backend (`models/PricingSettings.ts`) de propósito: a tela recalcula
 * enquanto a pessoa arrasta a margem, sem ida ao servidor. Um teste compara os dois corpos e
 * falha se divergirem — duas implementações separam-se no dia em que alguém mexe numa e esquece
 * a outra.
 */

export interface ConfiguracaoDePreco {
  id: string;
  taxPercentage: number;
  profitMarginPercentage: number;
  roundUpToNearest: number;
}

export interface PrecoCalculado {
  custo: number;
  imposto: number;
  margem: number;
  precoExato: number;
  preco: number;
  lucro: number;
  margemEfetiva: number;
}

export interface ExamePrecificado {
  providerExamId: string | null;
  examId: string;
  examName: string;
  baseCost: number | null;
  custo: number;
  precoAtual: number | null;
  calculo: PrecoCalculado | null;
  divergente: boolean;
}

function numero(valor: unknown): number {
  const n = Number(valor ?? 0);
  return Number.isFinite(n) ? n : 0;
}

function arredonda(valor: number): number {
  return Math.round(valor * 100) / 100;
}

export function calcularPreco(entrada: {
  custo: number;
  impostoPercentual: number;
  margemPercentual: number;
  arredondarPara?: number;
}): PrecoCalculado | null {
  const custo = numero(entrada.custo);
  const impostoPercentual = numero(entrada.impostoPercentual);
  const margemPercentual = numero(entrada.margemPercentual);

  if (custo < 0 || impostoPercentual < 0 || margemPercentual < 0) return null;

  // A soma e conferida em PORCENTAGEM, nao na fracao que sobra. `1 - 0.7 - 0.3` da 5,5e-17 em
  // ponto flutuante, e nao zero: testar `sobra <= 0` deixaria 70% + 30% passar e devolver um
  // preco absurdo, dividindo por um numero minusculo. Somar os percentuais evita a armadilha.
  if (impostoPercentual + margemPercentual >= 100) return null;

  const sobra = 1 - impostoPercentual / 100 - margemPercentual / 100;
  if (sobra <= 0) return null;

  const precoExato = arredonda(custo / sobra);

  const passo = numero(entrada.arredondarPara);
  const preco =
    passo > 0 ? arredonda(Math.ceil((precoExato - 1e-9) / passo) * passo) : precoExato;

  const imposto = arredonda(preco * (impostoPercentual / 100));
  const lucro = arredonda(preco - imposto - custo);

  return {
    custo: arredonda(custo),
    imposto,
    margem: margemPercentual,
    precoExato,
    preco,
    lucro,
    margemEfetiva: preco > 0 ? arredonda((lucro / preco) * 100) : 0,
  };
}

/** R$ com duas casas, no padrão brasileiro. */
export function emReais(valor: number | null | undefined): string {
  if (valor === null || valor === undefined) return '—';
  return valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}
